import { inject, Injectable } from '@angular/core';

import { environment } from '../../../../environments/environment';
import { EnvironmentConfigService } from '../../core/environment-config/environment-config.service';
import { BusinessStatusCodeEnum } from '../../shared/enums/business-status-code.enum';
import { HttpMethodEnum } from '../../shared/enums/http-method.enum';

import { LogLevelEnum } from './log-level.enum';
import { LogRequestModel } from './log-request.model';
import { LoggerService } from './logger.service';

@Injectable()
export class ConsoleLoggerService implements LoggerService {
  private readonly environmentConfigService = inject(EnvironmentConfigService);

  private get isDebugMode(): boolean {
    if (environment.deploymentTarget === 'onprem') {
      return environment.ENV === 'local';
    }
    const env = this.environmentConfigService.resolvedEnv;
    return env === 'local' || env === 'dev';
  }

  private checkLogLevel(logLevel: LogLevelEnum): boolean {
    const logLvl =
      environment.deploymentTarget === 'onprem'
        ? environment.LOG_LVL
        : (this.environmentConfigService.snapshot()?.LOG_LVL ?? 0);
    return logLevel >= Number.parseInt(String(logLvl), 10);
  }

  public debug(
    errorMessage: string,
    cause?: string,
    businessErrorId?: BusinessStatusCodeEnum,
    calledUrl?: string,
    httpRequestMethod?: HttpMethodEnum,
    stackTrace?: string,
  ): void | any {
    if (this.isDebugMode && this.checkLogLevel(LogLevelEnum.DEBUG)) {
      const newLogRequest: LogRequestModel = new LogRequestModel(
        LogLevelEnum.DEBUG,
        errorMessage,
        cause,
        businessErrorId,
        calledUrl,
        httpRequestMethod,
        stackTrace,
      );
      // TODO: try to log with context so that log will be shown with right source-file and correct line number - see previous revisions
      // eslint-disable-next-line no-console
      console.log(newLogRequest.toString());
    }
  }

  public info(
    errorMessage: string,
    cause?: string,
    businessErrorId?: BusinessStatusCodeEnum,
    calledUrl?: string,
    httpRequestMethod?: HttpMethodEnum,
    stackTrace?: string,
  ): void | any {
    if (this.isDebugMode && this.checkLogLevel(LogLevelEnum.INFO)) {
      const newLogRequest: LogRequestModel = new LogRequestModel(
        LogLevelEnum.INFO,
        errorMessage,
        cause,
        businessErrorId,
        calledUrl,
        httpRequestMethod,
        stackTrace,
      );
      // TODO: try to log with context so that log will be shown with right source-file and correct line number - see previous revisions
      // eslint-disable-next-line no-console
      console.log(newLogRequest.toString());
    }
  }

  public warn(
    errorMessage: string,
    cause?: string,
    businessErrorId?: BusinessStatusCodeEnum,
    calledUrl?: string,
    httpRequestMethod?: HttpMethodEnum,
    stackTrace?: string,
  ): void | any {
    // WARN/ERROR are intentionally NOT gated on isDebugMode: they must surface in every environment
    // (incl. cloud staging/prod and on-prem server), controlled only by LOG_LVL. DEBUG/INFO stay
    // debug-mode-only (local/dev). See TARE-18657.
    if (this.checkLogLevel(LogLevelEnum.WARN)) {
      const newLogRequest: LogRequestModel = new LogRequestModel(
        LogLevelEnum.WARN,
        errorMessage,
        cause,
        businessErrorId,
        calledUrl,
        httpRequestMethod,
        stackTrace,
      );
      // TODO: try to log with context so that log will be shown with right source-file and correct line number - see previous revisions
      // eslint-disable-next-line no-console
      console.warn(newLogRequest.toString());
    }
  }

  public error(
    errorMessage: string,
    cause?: string,
    businessErrorId?: BusinessStatusCodeEnum,
    calledUrl?: string,
    httpRequestMethod?: HttpMethodEnum,
    stackTrace?: string,
  ): void | any {
    // Not gated on isDebugMode (see warn()): errors surface in every environment, LOG_LVL-controlled.
    if (this.checkLogLevel(LogLevelEnum.ERROR)) {
      const newLogRequest: LogRequestModel = new LogRequestModel(
        LogLevelEnum.ERROR,
        errorMessage,
        cause,
        businessErrorId,
        calledUrl,
        httpRequestMethod,
        stackTrace,
      );
      // TODO: try to log with context so that log will be shown with right source-file and correct line number - see previous revisions
      // eslint-disable-next-line no-console
      console.error(newLogRequest.toString());
    }
  }
}
